/*
 * Official channels and channel links (services/api-go/internal/{officialchannels,dachannel,channelslug};
 * docs/OFFICIAL_CHANNELS.md).
 *
 * Channel links: channels are shared by their own link, web.dijitalapp.com/kanal/<slug> (as WhatsApp shares
 * channels), never by an @name: @names are people's (and the groups that have one). A slug is unique among
 * channels only; the username rules (reserved names, look-alikes) apply to it, so official names are staff's.
 *
 * "DijitalApp", the official channel: tips about features ("Biliyor muydunuz?"), release notes ("Yenilikler") and
 * Market app spotlights, as cards in messages.quake (the column every official channel's card uses) with a
 * readable text body for notifications, previews, search and older apps. Admin › Resmî kanallar
 * (officialchannels.manage).
 */

/** A channel link's slug: 3–32 of a–z, 0–9 and single dashes inside (CHANNEL_SLUG_PATTERN on the server). */
export const CHANNEL_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/;

export function isChannelSlug(s: string): boolean {
  return CHANNEL_SLUG_PATTERN.test(s) && !s.includes('--');
}

/** The web path of a channel link (/channel/<slug> is an alias the app sends on to it). */
export const channelPath = (slug: string) => `/kanal/${slug}`;

/** GET /channels/slug-check (and the panel's): whether a slug can be had, and why not. */
export interface ChannelSlugCheckDto {
  slug: string;
  available: boolean;
  reason: 'invalid' | 'taken' | 'reserved' | 'blocked' | null;
  /** A free slug made from ?title=, when asked. */
  suggestion?: string | null;
}

export type OfficialCardAccent = 'blue' | 'violet' | 'green' | 'orange' | 'pink' | 'teal';
export type OfficialCardPush = 'silent' | 'normal';

export interface OfficialBulletDto {
  /** A web app icon name (components/Icon.tsx). */
  icon: string;
  tr: string;
  en: string;
}

/** A "Biliyor muydunuz?" tip. */
export interface OfficialTipCardDto {
  v: 1;
  kind: 'tip';
  id: string;
  icon: string;
  accent: OfficialCardAccent;
  titleTr: string;
  titleEn: string;
  bodyTr: string;
  bodyEn: string;
  bullets: OfficialBulletDto[];
  /** Where the button opens in the app (absent: no button). */
  path?: string;
  ctaTr?: string;
  ctaEn?: string;
  push: OfficialCardPush;
  test?: boolean;
}

export type ReleaseItemKind = 'feature' | 'improvement' | 'fix' | 'security' | 'performance' | 'known_issue';

/** "Yenilikler": a published release note. v2: the short card (three highlights and a link to the full notes). */
export interface OfficialReleaseCardDto {
  v: 1 | 2;
  kind: 'release';
  version: string;
  /** YYYY-MM-DD */
  releasedOn: string;
  title: string;
  summary: string;
  items: { kind: ReleaseItemKind; text: string }[];
  /** Highlights not listed, and the number of fixes. */
  more: number;
  fixes: number;
  /** The full notes of this version in the app ("/yenilikler#v0.22.0"); v2 cards. */
  notesPath?: string;
  push: OfficialCardPush;
  test?: boolean;
}

/** A Market app spotlight. */
export interface OfficialSpotlightCardDto {
  v: 1;
  kind: 'spotlight';
  appId: string;
  icon: string;
  category: string;
  categoryTr: string;
  categoryEn: string;
  nameTr: string;
  nameEn: string;
  descTr: string;
  descEn: string;
  tier: 'free' | 'pro' | 'plus' | 'business';
  version: string | null;
  developer: string;
  developerVerified: boolean;
  push: OfficialCardPush;
  test?: boolean;
}

export type OfficialCardDto = OfficialTipCardDto | OfficialReleaseCardDto | OfficialSpotlightCardDto;

export interface OfficialTipDto {
  id: string;
  active: boolean;
  /** The feature flag the feature needs ('' for none): while off, the tip is not posted. */
  flag: string;
  icon: string;
  accent: OfficialCardAccent;
  titleTr: string;
  titleEn: string;
  bodyTr: string;
  bodyEn: string;
  bullets: OfficialBulletDto[];
  path: string;
  ctaTr: string;
  ctaEn: string;
}

export type OfficialOrder = 'sequential' | 'shuffle';

export interface DijitalAppChannelConfig {
  enabled: boolean;
  postsPerDay: number;
  startHour: number;
  endHour: number;
  minGapHours: number;
  spotlightsEnabled: boolean;
  spotlightEvery: number;
  noRepeatWithin: number;
  freeAppsOnly: boolean;
  appOrder: OfficialOrder;
  releasesEnabled: boolean;
  notifyReleases: boolean;
  tipOrder: OfficialOrder;
  tips: OfficialTipDto[];
}

/** The ranges the panel offers (the server checks the same). */
export const DIJITALAPP_CHANNEL_BOUNDS = {
  postsPerDay: [1, 6],
  hour: [0, 23],
  minGapHours: [1, 24],
  spotlightEvery: [2, 5],
  noRepeatWithin: [1, 20],
  tips: 40,
  bullets: 4,
} as const;

export const OFFICIAL_CARD_ACCENTS: readonly OfficialCardAccent[] = ['blue', 'violet', 'green', 'orange', 'pink', 'teal'];

export interface OfficialPersonDto {
  id: string;
  username: string;
  displayName: string;
}

export type OfficialChannelLogAction = 'posted' | 'skipped' | 'test' | 'created' | 'slug' | 'error';
export type OfficialChannelWarning = 'no_official_account' | 'no_channel' | 'channel_deleted' | 'channels_off' | 'owner_no_device' | 'no_slug';
export type OfficialPostKind = 'tip' | 'release' | 'spotlight';

export interface AdminDijitalAppChannelDto {
  config: DijitalAppChannelConfig;
  defaults: DijitalAppChannelConfig;
  channel: {
    id: string;
    title: string;
    description: string | null;
    slug: string | null;
    verified: boolean;
    followers: number;
    hasPhoto: boolean;
    owner: OfficialPersonDto | null;
    admins: OfficialPersonDto[];
    createdAt: string;
  } | null;
  official: OfficialPersonDto | null;
  warnings: OfficialChannelWarning[];
  stats: {
    postsToday: number;
    lastPostedAt: string | null;
    postsSinceSpotlight: number;
    /** Why nothing goes out right now ('' when the next run may post). */
    wait: '' | 'off' | 'quiet_hours' | 'daily_limit' | 'gap';
    nextKind: OfficialPostKind | '';
    nextRef: string;
  };
  tips: { id: string; flagOn: boolean; posts: number; lastPostedAt: string | null }[];
  apps: { id: string; icon: string; name: string; tier: string; ready: boolean; enabled: boolean; usable: boolean; posts: number; lastPostedAt: string | null }[];
  releases: { version: string; releasedOn: string; title: string; posted: boolean }[];
  /** The icon names a tip may use. */
  icons: string[];
  log: {
    id: number;
    action: OfficialChannelLogAction;
    kind: OfficialPostKind | null;
    ref: string | null;
    messageId: string | null;
    reason: string;
    actor: string | null;
    at: string;
  }[];
}
